import get from 'lodash/get';
import to from '~/utils/to';
import getDayOfWeek from '~/utils/getDayOfWeek';
import localeToLanguage from '~/helpers/localeToLanguage';

export const state = () => ({
  languages: [],
  language: {},
  airports: [],
  airport: {},
  airportRouteContent: null,
  translations: {},
  pageContent: {},
  airportDataContentLanguages: [],
  headerAirportsConfiguration: {},
  parkings: [],
  merchants: [],
  slugs: [],
  openingHours: [],
  flags: {
    experiments: {},
    features: {},
  },
});

export const mutations = {
  assignDataForDABucket(state, bucketState) {
    // domain
    bucketState.domain.country = state.language.country.name;
    bucketState.domain.country_code = state.language.country.code;
    bucketState.domain.language = state.language.lang;
    bucketState.domain.name = state.language.domain;
    //
    bucketState.airport.name = state.airport.name;
    bucketState.airport.id = state.airport.id;
    bucketState.airport.slug = state.airport.slug;
    bucketState.airport.commonName = state.airport.commonName;
    bucketState.airport.devtitle = state.airport.devtitle;
    //
    bucketState.currency = state.language.currency.iso_code;
    //
  },
};

export const getters = {
  experimentVariant: (state) => (name) => state.flags.features?.[name],
  isExperimentEnabled: (state) => (name) => {
    const { features } = state.flags;

    return !!features?.[name];
  },
};

export const actions = {
  experimentVariant({ state, commit }, { name = '', condition = true }) {
    if (!condition) {
      return undefined;
    }
    commit('experiments/addExperiment', {
      name,
      variant: state.flags.features?.[name],
    });
    return state.flags.features?.[name];
  },
  // eslint-disable-next-line no-shadow
  async nuxtServerInit({ commit, state }, { app, route, req }) {
    try {
      if (req.headers?.['x-features']) {
        state.flags.features = JSON.parse(req.headers['x-features']);
      }

      if (req.headers?.['x-experiments']) {
        const result = {};
        req.headers?.['x-experiments'].split(',').forEach((element) => {
          const [elKey, elValue] = element.split('=');
          result[elKey] = elValue;
        });
        state.flags.experiments = result;
      }
    } catch (error) {
      console.error(error);
    }
    const languages = await app.$pbx.getLanguages();
    const LANG_IETF = localeToLanguage(req.headers['x-locale'] || process.env.LOCALE_DEFAULT);

    state.language = languages.find(({ lang }) => lang === LANG_IETF);
    state.languages = languages.map((lang) => ({
      ...lang,
      domain: `${app.$paths.urlEnvPrefix}${lang.domain}`,
    }));

    state.airports = await this.app.$pbx.getAirports(state.language?.lang ?? 'en-us');
    state.airport = state.airports.find(({ slug }) => slug === route.params.airport);

    if (!state.airport) {
      const err = new Error('Page not found');
      err.statusCode = 404;
      throw err;
    }

    const [, merchants] = await to(this.app.$pbx.getMerchants(state.airport.id, state.language.id));
    state.merchants = merchants ?? [];

    const [, airportRouteContent] = await to(
      this.app.$pbx.getLocationRouteContent(state.airport.id, state.language.id),
    );
    state.airportRouteContent = airportRouteContent ?? null;

    const { lang } = state.language;

    if (!state.language.socials.facebook) {
      delete state.language.socials.facebook;
    }

    if (!state.language.socials.twitter) {
      delete state.language.socials.twitter;
    }

    // If no YouTube link is set in the socials fallback to the default
    if (!state.language.socials.youtube) {
      state.language.socials.youtube = 'https://www.youtube.com/parkosnl';
    }

    state.translations = await app.$pbx.getTranslations(state.language.lang);
    state.pageContent = {
      termsConditionsContent: { [lang]: (await app.$pbx.getPageContent('terms-conditions'))[lang] },
      privacyPolicyContent: { [lang]: (await app.$pbx.getPageContent('privacy-policy'))[lang] },
      aboutPageContent: { [lang]: (await app.$pbx.getPageContent('about-us'))[lang] },
    };

    const { content } = await app.$pbx.getAirportData(state.airport);
    if (content) {
      Object.keys(content).forEach((langKey) => {
        content[langKey].url = content[langKey].url.replace(/https:\/\//, `https://${app.$paths.urlEnvPrefix}`);
      });
      state.airportDataContentLanguages = content;
    }

    state.headerAirportsConfiguration = await app.$pbx.getHeaderAirportsConfiguration();

    const entries = ['footer_airports', 'footer_information', 'footer_about', 'footer_icons'].map(async (slug) => [
      slug,
      await app.$pbx.getPageTemplate(slug, state.language.lang),
    ]);

    state.pageTemplate = Object.fromEntries(await Promise.all(entries));

    state.metaPages = {
      faqMeta: (await app.$pbx.getPageContent('faq'))[state.language.lang],
    };

    // state.parkings = await this.app.$pbx.getAirportParkings(state.language, state.airport);
    state.slugs = await this.$pbx.getTranslationValues('map-slug');

    const [, openingHoursData] = await to(this.app.$pbx.getOpeningHours(lang));
    const openingHours = get(openingHoursData, [lang, 'openingHours'], []);

    state.openingHours = openingHours.map((day) => ({
      title: getDayOfWeek(day.dayOfWeek),
      value: `${day.startTime} - ${day.endTime}`,
    }));

    commit('assignDataForDABucket', state.bucket);
  },
};

export const strict = false;
